import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  SearchIcon,
  BuildingIcon,
  UserIcon,
  LockIcon,
  UnlockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FilterIcon,
  DownloadIcon,
  RefreshCwIcon,
  UsersIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
  KeyIcon,
  LayoutGrid,
  List,
  InfoIcon,
  LogInIcon,
  XIcon,
  Loader2Icon,
  MoreVerticalIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraUserManagementNew = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [companyStatusFilter, setCompanyStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [confirmModal, setConfirmModal] = useState({ show: false, userId: null, action: null, userName: '' });
  const [passwordModal, setPasswordModal] = useState({ show: false, user: null, password: null, loading: false });
  const [userPasswords, setUserPasswords] = useState({}); // Store revealed passwords
  const [userDetailModal, setUserDetailModal] = useState({ show: false, user: null, companySubscription: null });
  const [impersonateModal, setImpersonateModal] = useState({ show: false, user: null, loading: false, password: '' });
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // Track which row's menu is open
  const [usersPerPage, setUsersPerPage] = useState(25);
  const [masterPasswordModal, setMasterPasswordModal] = useState({ show: false, user: null, password: null, loading: false, hasMasterPassword: false });

  useEffect(() => {
    fetchCompaniesWithUsers();
  }, []);

  const fetchCompaniesWithUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch all companies
      const companiesResponse = await axios.get(`${BACKEND_URL}/api/ultra-admin/tenants`, config);
      const companiesData = companiesResponse.data.tenants;

      // Fetch all users
      const usersResponse = await axios.get(`${BACKEND_URL}/api/ultra-admin/users`, config);
      const usersData = usersResponse.data.users;

      // Group users by company
      const companiesWithUsers = companiesData.map(company => {
        const companyUsers = usersData.filter(user => user.company_id === company.id);
        return {
          ...company,
          users: companyUsers,
          activeUserCount: companyUsers.filter(u => u.is_active).length,
          inactiveUserCount: companyUsers.filter(u => !u.is_active).length
        };
      });

      // Sort by user count (descending)
      companiesWithUsers.sort((a, b) => b.users.length - a.users.length);

      setCompanies(companiesWithUsers);
      setUsers(usersData);
      
      // If a company is already selected, keep it. Otherwise, auto-select first company
      if (selectedCompanyId) {
        // Check if currently selected company still exists
        const stillExists = companiesWithUsers.some(c => c.id === selectedCompanyId);
        if (!stillExists && companiesWithUsers.length > 0) {
          // If selected company no longer exists, select first one
          setSelectedCompanyId(companiesWithUsers[0].id);
        }
        // Otherwise keep current selection
      } else if (companiesWithUsers.length > 0) {
        // No company selected yet, select first one
        setSelectedCompanyId(companiesWithUsers[0].id);
      }
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showConfirmModal = (userId, action, userName) => {
    setConfirmModal({ show: true, userId, action, userName });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ show: false, userId: null, action: null, userName: '' });
  };

  const resetUserPassword = async (user) => {
    setPasswordModal({ show: true, user, password: null, loading: true });
    
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store the password for this user
      setUserPasswords(prev => ({
        ...prev,
        [user.id]: response.data.temp_password
      }));
      
      setPasswordModal({ 
        show: true, 
        user, 
        password: response.data.temp_password, 
        loading: false 
      });
      
      toast.success(`${user.full_name} için yeni şifre oluşturuldu`);
      
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Şifre sıfırlanırken hata oluştu');
      setPasswordModal({ show: false, user: null, password: null, loading: false });
    }
  };

  const handleMasterPassword = async (user) => {
    setMasterPasswordModal({ show: true, user, password: null, loading: true, hasMasterPassword: false });
    
    try {
      const token = localStorage.getItem('ultra_admin_token');
      
      // First check if user has master password
      const checkResponse = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/users/${user.id}/master-password`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMasterPasswordModal({ 
        show: true, 
        user, 
        password: null, 
        loading: false, 
        hasMasterPassword: checkResponse.data.has_master_password 
      });
      
    } catch (error) {
      console.error('Master password check error:', error);
      toast.error('Master şifre kontrol edilirken hata oluştu');
      setMasterPasswordModal({ show: false, user: null, password: null, loading: false, hasMasterPassword: false });
    }
  };

  const generateMasterPassword = async () => {
    if (!masterPasswordModal.user) return;
    
    setMasterPasswordModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/users/${masterPasswordModal.user.id}/generate-master-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMasterPasswordModal({ 
        show: true, 
        user: masterPasswordModal.user, 
        password: response.data.master_password, 
        loading: false, 
        hasMasterPassword: true 
      });
      
      toast.success(`${masterPasswordModal.user.full_name} için master şifre oluşturuldu`);
      
    } catch (error) {
      console.error('Master password generation error:', error);
      toast.error('Master şifre oluşturulurken hata oluştu');
      setMasterPasswordModal(prev => ({ ...prev, loading: false }));
    }
  };

  const copyMasterPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('Master şifre kopyalandı!');
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('Şifre kopyalandı!');
  };

  const togglePasswordVisibility = (userId) => {
    if (!userPasswords[userId]) {
      // If password not yet revealed, reset it
      const user = users.find(u => u.id === userId);
      if (user) {
        resetUserPassword(user);
      }
    } else {
      // Password already revealed, just toggle visibility or remove it
      setUserPasswords(prev => {
        const newPasswords = { ...prev };
        delete newPasswords[userId];
        return newPasswords;
      });
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${BACKEND_URL}/api/ultra-admin/users/${userId}/actions`, { action }, config);
      
      const successMessages = {
        'deactivate': 'Kullanıcı başarıyla pasifleştirildi',
        'activate': 'Kullanıcı başarıyla aktifleştirildi'
      };
      
      toast.success(successMessages[action] || 'İşlem başarılı');
      closeConfirmModal();
      fetchCompaniesWithUsers();
    } catch (error) {
      toast.error(`İşlem başarısız: ${error.response?.data?.detail || error.message}`);
      closeConfirmModal();
    }
  };

  const showUserDetail = async (user) => {
    setUserDetailModal({ show: true, user, companySubscription: null });
    
    // Fetch company subscription info
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/tenants/${user.company_id}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const companyData = response.data;
      
      // Calculate days remaining
      let daysRemaining = null;
      if (companyData.subscription_end) {
        const endDate = new Date(companyData.subscription_end);
        const today = new Date();
        daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      } else if (companyData.trial_end) {
        const endDate = new Date(companyData.trial_end);
        const today = new Date();
        daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      }
      
      setUserDetailModal({ 
        show: true, 
        user, 
        companySubscription: {
          subscription_type: companyData.subscription_type || 'N/A',
          subscription_end: companyData.subscription_end,
          trial_end: companyData.trial_end,
          days_remaining: daysRemaining
        }
      });
      
    } catch (error) {
      console.error('Error fetching company subscription:', error);
      // Keep modal open even if subscription fetch fails
    }
  };

  const closeUserDetail = () => {
    setUserDetailModal({ show: false, user: null, companySubscription: null });
  };

  const handleImpersonate = async (user) => {
    setImpersonateModal({ show: true, user, loading: false, password: '' });
  };

  const closeImpersonateModal = () => {
    setImpersonateModal({ show: false, user: null, loading: false, password: '' });
  };

  const performImpersonation = async () => {
    // Password-based impersonation
    if (!impersonateModal.password) {
      toast.error('Lütfen şifrenizi girin');
      return;
    }

    console.log('Starting impersonation for user:', impersonateModal.user.email);
    setImpersonateModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('ultra_admin_token');
      console.log('Ultra admin token:', token?.substring(0, 20) + '...');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ultra-admin/impersonate`,
        {
          user_id: impersonateModal.user.id,
          password: impersonateModal.password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Impersonation response:', response.data);

      if (response.data.success) {
        // Open new tab with user session
        const userToken = response.data.user_token;
        const impersonateUrl = `${window.location.origin}/dashboard?impersonate_token=${userToken}`;
        
        console.log('Opening impersonate URL:', impersonateUrl.substring(0, 80) + '...');
        window.open(impersonateUrl, '_blank');
        
        toast.success(`${impersonateModal.user.full_name} hesabına geçiş yapıldı`);
        closeImpersonateModal();
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Geçiş başarısız oldu');
      setImpersonateModal(prev => ({ ...prev, loading: false }));
    }
  };

  const toggleCompanyExpand = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  // Get selected company or all users
  const selectedCompany = selectedCompanyId === 'all' ? null : companies.find(c => c.id === selectedCompanyId);
  
  // Filter users based on search and status
  const filteredUsers = (() => {
    let usersToFilter = selectedCompanyId === 'all' ? users : (selectedCompany?.users || []);
    
    return usersToFilter.filter(user => {
      const matchesUserSearch = userSearchQuery === '' || 
                               user.full_name.toLowerCase().includes(userSearchQuery.toLowerCase());
      const matchesEmailSearch = emailSearchQuery === '' || 
                                user.email.toLowerCase().includes(emailSearchQuery.toLowerCase());
      const matchesStatus = userStatusFilter === 'all' || 
                           (userStatusFilter === 'active' && user.is_active) ||
                           (userStatusFilter === 'inactive' && !user.is_active);
      return matchesUserSearch && matchesEmailSearch && matchesStatus;
    });
  })();

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCwIcon className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Kullanıcılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h2>
            <p className="text-gray-600 mt-1">Tüm platform kullanıcılarını şirketlere göre yönetin</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={fetchCompaniesWithUsers}>
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button variant="outline">
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Toplam Şirket</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{companies.length}</p>
              </div>
              <BuildingIcon className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Toplam Kullanıcı</p>
                <p className="text-2xl font-bold text-green-900 mt-1">{users.length}</p>
              </div>
              <UsersIcon className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Aktif Kullanıcı</p>
                <p className="text-2xl font-bold text-emerald-900 mt-1">
                  {users.filter(u => u.is_active).length}
                </p>
              </div>
              <UserIcon className="w-8 h-8 text-emerald-400" />
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Pasif Kullanıcı</p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {users.filter(u => !u.is_active).length}
                </p>
              </div>
              <LockIcon className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Company List */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="p-4">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <BuildingIcon className="w-5 h-5 mr-2 text-purple-600" />
                Şirketler ({companies.length})
              </h3>
              
              {/* Search */}
              <div className="relative mb-3">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Şirket ara..."
                  value={companySearchQuery}
                  onChange={(e) => setCompanySearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10"
                />
                {companySearchQuery && (
                  <button
                    onClick={() => setCompanySearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="space-y-2">
                <select
                  value={companyStatusFilter}
                  onChange={(e) => setCompanyStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">🔵 Tüm Durumlar</option>
                  <option value="active">✅ Aktif</option>
                  <option value="trial">🎁 Deneme</option>
                  <option value="suspended">⏸️ Askıda</option>
                  <option value="expired">❌ Süresi Dolmuş</option>
                </select>

                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">📦 Tüm Planlar</option>
                  <option value="free_trial">🆓 Free Trial</option>
                  <option value="starter">🥉 Starter</option>
                  <option value="professional">🥈 Professional</option>
                  <option value="enterprise">🥇 Enterprise</option>
                </select>
              </div>

              {/* Filtered count */}
              <div className="text-xs text-gray-500 mt-2 mb-2">
                {companies.filter(company => {
                  const matchesSearch = (company.company_name || '').toLowerCase().includes(companySearchQuery.toLowerCase());
                  const matchesStatus = companyStatusFilter === 'all' || company.status === companyStatusFilter;
                  const matchesPlan = planFilter === 'all' || company.subscription_plan === planFilter;
                  return matchesSearch && matchesStatus && matchesPlan;
                }).length} şirket gösteriliyor
              </div>
            </div>

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {/* All Users Option */}
              <div
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedCompanyId === 'all'
                    ? 'bg-blue-100 border-2 border-blue-600'
                    : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                }`}
                onClick={() => {
                  setSelectedCompanyId('all');
                  setCurrentPage(1);
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedCompanyId === 'all' ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <UsersIcon className={`w-5 h-5 ${
                        selectedCompanyId === 'all' ? 'text-white' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900">
                        Tüm Kullanıcılar
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {users.length} toplam kullanıcı
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Companies List */}
              {companies
                .filter(company => {
                  // Filter by search
                  const matchesSearch = (company.company_name || '').toLowerCase().includes(companySearchQuery.toLowerCase());
                  
                  // Filter by status
                  const matchesStatus = companyStatusFilter === 'all' || company.status === companyStatusFilter;
                  
                  // Filter by plan
                  const matchesPlan = planFilter === 'all' || company.subscription_plan === planFilter;
                  
                  return matchesSearch && matchesStatus && matchesPlan;
                })
                .map(company => (
                <div
                  key={company.id}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedCompanyId === company.id
                      ? 'bg-purple-100 border-2 border-purple-600'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                  onClick={() => {
                    setSelectedCompanyId(company.id);
                    setCurrentPage(1);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedCompanyId === company.id ? 'bg-purple-600' : 'bg-gray-200'
                      }`}>
                        <BuildingIcon className={`w-5 h-5 ${
                          selectedCompanyId === company.id ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {company.company_name}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {company.users.length} kullanıcı
                          </Badge>
                          <Badge 
                            className={`text-xs ${
                              company.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {company.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right Panel - Users List */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="p-6">
            {selectedCompanyId ? (
              <>
                {/* Company Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {selectedCompanyId === 'all' ? 'Tüm Kullanıcılar' : selectedCompany?.company_name}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {selectedCompanyId === 'all' 
                          ? `${filteredUsers.length} kullanıcı` 
                          : `${filteredUsers.length} kullanıcı (${selectedCompany?.activeUserCount || 0} aktif, ${selectedCompany?.inactiveUserCount || 0} pasif)`
                        }
                      </p>
                    </div>
                    {selectedCompanyId !== 'all' && selectedCompany && (
                      <Badge className="bg-purple-100 text-purple-700">
                        {selectedCompany.subscription_plan}
                      </Badge>
                    )}
                  </div>

                  {/* Filters Row 1 - Search Filters */}
                  <div className="flex space-x-3 mb-3">
                    {/* User Name Search */}
                    <div className="relative flex-1">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Kullanıcı ara..."
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-10 pr-10"
                      />
                      {userSearchQuery && (
                        <button
                          onClick={() => {
                            setUserSearchQuery('');
                            setCurrentPage(1);
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Email Search */}
                    <div className="relative flex-1">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="E-posta adresi ara..."
                        value={emailSearchQuery}
                        onChange={(e) => {
                          setEmailSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-10 pr-10"
                      />
                      {emailSearchQuery && (
                        <button
                          onClick={() => {
                            setEmailSearchQuery('');
                            setCurrentPage(1);
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filters Row 2 - Status, Per Page, View Mode */}
                  <div className="flex items-center space-x-3">
                    {/* Status Filter */}
                    <select
                      value={userStatusFilter}
                      onChange={(e) => {
                        setUserStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Tüm Durumlar</option>
                      <option value="active">Aktif</option>
                      <option value="inactive">Pasif</option>
                    </select>
                    
                    {/* Per Page Dropdown */}
                    <select
                      value={usersPerPage}
                      onChange={(e) => {
                        setUsersPerPage(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="25">25 / sayfa</option>
                      <option value="50">50 / sayfa</option>
                      <option value="100">100 / sayfa</option>
                    </select>

                    {/* Refresh Button */}
                    <Button
                      onClick={fetchCompaniesWithUsers}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <RefreshCwIcon className="w-4 h-4" />
                      Yenile
                    </Button>
                    
                    {/* View Mode Toggle */}
                    <div className="flex border border-gray-300 rounded-md overflow-hidden ml-auto">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 flex items-center gap-2 transition-colors ${
                          viewMode === 'grid'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Grid Görünümü"
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 flex items-center gap-2 transition-colors border-l border-gray-300 ${
                          viewMode === 'list'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Liste Görünümü"
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Users List View */}
                {viewMode === 'list' && (
                  <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
                    <table className="w-full" style={{ overflow: 'visible' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kullanıcı</th>
                        {selectedCompanyId === 'all' && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şirket</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şifre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentUsers.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <UserIcon className="w-5 h-5 text-purple-600" />
                              </div>
                              <div className="min-w-0 overflow-hidden" style={{ maxWidth: '250px' }}>
                                <p className="font-medium text-gray-900 text-sm truncate overflow-hidden" title={user.full_name}>{user.full_name}</p>
                                <p className="text-xs text-gray-500 truncate overflow-hidden break-all" title={user.email} style={{ wordBreak: 'break-word' }}>{user.email}</p>
                              </div>
                            </div>
                          </td>
                          {selectedCompanyId === 'all' && (
                            <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                              <div className="truncate overflow-hidden" title={companies.find(c => c.id === user.company_id)?.company_name || 'N/A'}>
                                <Badge variant="outline" className="truncate max-w-full">
                                  {companies.find(c => c.id === user.company_id)?.company_name || 'N/A'}
                                </Badge>
                              </div>
                            </td>
                          )}
                          <td className="px-4 py-3" style={{ maxWidth: '180px' }}>
                            <div className="truncate overflow-hidden" title={user.role_name || user.role_id}>
                              <Badge variant="outline" className="truncate max-w-full">{user.role_name || user.role_id}</Badge>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {userPasswords[user.id] ? (
                                <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2">
                                  <code className="text-sm font-mono text-gray-800">
                                    {userPasswords[user.id]}
                                  </code>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyPassword(userPasswords[user.id])}
                                    className="p-1 h-auto text-gray-500 hover:text-gray-700"
                                  >
                                    <CopyIcon className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => togglePasswordVisibility(user.id)}
                                    className="p-1 h-auto text-gray-500 hover:text-gray-700"
                                  >
                                    <EyeOffIcon className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleMasterPassword(user)}
                                  className="p-2 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                                  title="Master Şifre"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {user.is_active ? (
                              <Badge className="bg-green-100 text-green-700">Aktif</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Pasif</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative" style={{ overflow: 'visible' }}>
                              <button
                                onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <MoreVerticalIcon className="w-5 h-5 text-gray-600" />
                              </button>

                              {/* Dropdown Menu */}
                              {actionMenuOpen === user.id && (
                                <>
                                  {/* Backdrop to close menu */}
                                  <div 
                                    className="fixed inset-0 z-40" 
                                    onClick={() => setActionMenuOpen(null)}
                                  />
                                  
                                  {/* Menu */}
                                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-1 z-50 max-h-96 overflow-y-auto"
                                       style={{ transform: 'translateY(0)' }}>
                                    <button
                                      onClick={() => {
                                        showUserDetail(user);
                                        setActionMenuOpen(null);
                                      }}
                                      className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                                    >
                                      <InfoIcon className="w-4 h-4 mr-3" />
                                      Detay
                                    </button>
                                    
                                    <button
                                      onClick={() => {
                                        handleImpersonate(user);
                                        setActionMenuOpen(null);
                                      }}
                                      className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                                    >
                                      <LogInIcon className="w-4 h-4 mr-3" />
                                      Kullanıcıya Geç
                                    </button>

                                    <button
                                      onClick={() => {
                                        handleMasterPassword(user);
                                        setActionMenuOpen(null);
                                      }}
                                      className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                                    >
                                      <KeyIcon className="w-4 h-4 mr-3" />
                                      Master Şifre
                                    </button>

                                    <div className="border-t border-gray-200 my-1"></div>

                                    {user.is_active ? (
                                      <button
                                        onClick={() => {
                                          showConfirmModal(user.id, 'deactivate', user.full_name);
                                          setActionMenuOpen(null);
                                        }}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                      >
                                        <LockIcon className="w-4 h-4 mr-3" />
                                        Pasifleştir
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          showConfirmModal(user.id, 'activate', user.full_name);
                                          setActionMenuOpen(null);
                                        }}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-green-600 hover:bg-green-50"
                                      >
                                        <UnlockIcon className="w-4 h-4 mr-3" />
                                        Aktifleştir
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                )}

                {/* Users Grid View */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentUsers.map(user => (
                      <Card key={user.id} className="p-4 hover:shadow-lg transition-shadow min-w-[280px]">
                        <div className="space-y-3">
                          {/* User Header */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center space-x-2 min-w-0 flex-1 overflow-hidden">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-semibold text-sm">
                                  {user.full_name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <p className="font-semibold text-sm text-gray-900 truncate overflow-hidden" title={user.full_name}>
                                  {user.full_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate overflow-hidden break-all" title={user.email} style={{ wordBreak: 'break-word' }}>
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            {user.is_active ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium whitespace-nowrap flex-shrink-0">
                                ✓ Aktif
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium whitespace-nowrap flex-shrink-0">
                                ✗ Pasif
                              </span>
                            )}
                          </div>

                          {/* User Details */}
                          <div className="space-y-1.5 text-xs">
                            {selectedCompanyId === 'all' && (
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-gray-500 flex-shrink-0">Şirket:</span>
                                <span className="font-medium text-gray-900 text-[11px] truncate overflow-hidden text-right max-w-[65%]" title={companies.find(c => c.id === user.company_id)?.company_name || 'N/A'}>
                                  {companies.find(c => c.id === user.company_id)?.company_name || 'N/A'}
                                </span>
                              </div>
                            )}
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-gray-500 flex-shrink-0">Rol:</span>
                              <span className="font-medium text-gray-900 text-[11px] truncate overflow-hidden text-right max-w-[65%]" title={user.role_name || user.role_id}>
                                {user.role_name || user.role_id}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 flex-shrink-0">Departman:</span>
                              <span className="font-medium text-gray-900 text-[11px] truncate overflow-hidden text-right max-w-[65%]" title={user.department || 'N/A'}>
                                {user.department || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 flex-shrink-0">Çalışan ID:</span>
                              <span className="font-medium text-gray-900 font-mono text-[10px] truncate overflow-hidden text-right max-w-[65%]" title={user.employee_id || 'N/A'}>
                                {user.employee_id || 'N/A'}
                              </span>
                            </div>
                          </div>

                          {/* Password Section */}
                          <div className="border-t pt-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">Master Şifre:</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMasterPassword(user)}
                                className="text-indigo-600 hover:bg-indigo-50 h-7 px-2"
                              >
                                <EyeIcon className="w-3 h-3 mr-1" />
                                <span className="text-xs">Oluştur/Göster</span>
                              </Button>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="space-y-2 pt-2 border-t">
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => showUserDetail(user)}
                                className="flex-1 text-blue-600 hover:bg-blue-50"
                              >
                                <InfoIcon className="w-4 h-4 mr-1" />
                                Detay
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleImpersonate(user)}
                                className="flex-1 text-purple-600 hover:bg-purple-50"
                              >
                                <LogInIcon className="w-4 h-4 mr-1" />
                                Geçiş
                              </Button>
                            </div>
                            <div className="flex space-x-2">
                              {user.is_active ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => showConfirmModal(user.id, 'deactivate', user.full_name)}
                                  className="flex-1 text-red-600 hover:bg-red-50"
                                >
                                  <LockIcon className="w-4 h-4 mr-1" />
                                  Pasifleştir
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => showConfirmModal(user.id, 'activate', user.full_name)}
                                  className="flex-1 text-green-600 hover:bg-green-50"
                                >
                                  <UnlockIcon className="w-4 h-4 mr-1" />
                                  Aktifleştir
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} / {filteredUsers.length} kullanıcı
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                      >
                        Önceki
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            if (totalPages <= 7) return true;
                            if (page === 1 || page === totalPages) return true;
                            if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                            return false;
                          })
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span className="px-2 text-gray-400">...</span>
                              )}
                              <Button
                                size="sm"
                                variant={currentPage === page ? 'default' : 'outline'}
                                onClick={() => setCurrentPage(page)}
                                className={currentPage === page ? 'bg-purple-600' : ''}
                              >
                                {page}
                              </Button>
                            </React.Fragment>
                          ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                      >
                        Sonraki
                      </Button>
                    </div>
                  </div>
                )}

                {currentUsers.length === 0 && (
                  <div className="text-center py-12">
                    <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Kullanıcı bulunamadı</h3>
                    <p className="text-gray-500">Arama kriterlerinize uygun kullanıcı yok.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Şirket seçin</h3>
                <p className="text-gray-500">Kullanıcıları görüntülemek için sol taraftan bir şirket seçin.</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                confirmModal.action === 'deactivate' ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {confirmModal.action === 'deactivate' ? (
                  <LockIcon className="w-8 h-8 text-red-600" />
                ) : (
                  <UnlockIcon className="w-8 h-8 text-green-600" />
                )}
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.action === 'deactivate' ? 'Kullanıcıyı Pasifleştir' : 'Kullanıcıyı Aktifleştir'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                <strong>{confirmModal.userName}</strong> kullanıcısını {
                  confirmModal.action === 'deactivate' ? 'pasifleştirmek' : 'aktifleştirmek'
                } istediğinizden emin misiniz?
              </p>

              {confirmModal.action === 'deactivate' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-700">
                    ⚠️ Pasif kullanıcı sisteme giriş yapamayacak.
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={closeConfirmModal}
                >
                  İptal
                </Button>
                <Button 
                  className={`flex-1 ${
                    confirmModal.action === 'deactivate' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                  } text-white`}
                  onClick={() => handleUserAction(confirmModal.userId, confirmModal.action)}
                >
                  {confirmModal.action === 'deactivate' ? 'Evet, Pasifleştir' : 'Evet, Aktifleştir'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {userDetailModal.show && userDetailModal.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Kullanıcı Detayları</h3>
                <button
                  onClick={closeUserDetail}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* User Header */}
                <div className="flex items-center space-x-4 pb-4 border-b">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">
                      {userDetailModal.user.full_name?.charAt(0).toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{userDetailModal.user.full_name}</h4>
                    <p className="text-sm text-gray-500">{userDetailModal.user.email}</p>
                    {userDetailModal.user.is_active ? (
                      <Badge className="mt-1 bg-green-100 text-green-700">Aktif</Badge>
                    ) : (
                      <Badge className="mt-1 bg-red-100 text-red-700">Pasif</Badge>
                    )}
                  </div>
                </div>

                {/* User Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Rol</label>
                    <p className="text-sm font-medium text-gray-900">{userDetailModal.user.role_name || userDetailModal.user.role_id}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Departman</label>
                    <p className="text-sm font-medium text-gray-900">{userDetailModal.user.department || 'N/A'}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Çalışan ID</label>
                    <p className="text-sm font-medium text-gray-900 font-mono">{userDetailModal.user.employee_id || 'N/A'}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Telefon</label>
                    <p className="text-sm font-medium text-gray-900">{userDetailModal.user.phone || 'N/A'}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Pozisyon</label>
                    <p className="text-sm font-medium text-gray-900">{userDetailModal.user.position || 'N/A'}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Durum</label>
                    <p className="text-sm font-medium text-gray-900">{userDetailModal.user.is_active ? 'Aktif' : 'Pasif'}</p>
                  </div>

                  <div className="space-y-1 col-span-2">
                    <label className="text-xs font-medium text-gray-500">Kayıt Tarihi</label>
                    <p className="text-sm font-medium text-gray-900">
                      {userDetailModal.user.created_at ? new Date(userDetailModal.user.created_at).toLocaleString('tr-TR') : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Company Subscription Info */}
                {userDetailModal.companySubscription && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Şirket Abonelik Bilgileri</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-blue-700">Abonelik Türü</label>
                        <p className="text-sm font-semibold text-blue-900">
                          {userDetailModal.companySubscription.subscription_type === 'monthly' ? 'Aylık' : 
                           userDetailModal.companySubscription.subscription_type === 'yearly' ? 'Yıllık' : 
                           userDetailModal.companySubscription.subscription_type === 'free_trial' ? 'Deneme' : 
                           userDetailModal.companySubscription.subscription_type}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs font-medium text-blue-700">Bitiş Tarihi</label>
                        <p className="text-sm font-semibold text-blue-900">
                          {userDetailModal.companySubscription.subscription_end 
                            ? new Date(userDetailModal.companySubscription.subscription_end).toLocaleDateString('tr-TR')
                            : userDetailModal.companySubscription.trial_end
                            ? new Date(userDetailModal.companySubscription.trial_end).toLocaleDateString('tr-TR')
                            : 'N/A'}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs font-medium text-blue-700">Kalan Gün</label>
                        <p className={`text-sm font-bold ${
                          userDetailModal.companySubscription.days_remaining !== null
                            ? userDetailModal.companySubscription.days_remaining > 7
                              ? 'text-green-600'
                              : userDetailModal.companySubscription.days_remaining > 0
                              ? 'text-orange-600'
                              : 'text-red-600'
                            : 'text-gray-900'
                        }`}>
                          {userDetailModal.companySubscription.days_remaining !== null
                            ? userDetailModal.companySubscription.days_remaining > 0
                              ? `${userDetailModal.companySubscription.days_remaining} gün`
                              : userDetailModal.companySubscription.days_remaining === 0
                              ? 'Bugün bitiyor'
                              : 'Süresi dolmuş'
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={closeUserDetail}
                  >
                    Kapat
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => {
                      closeUserDetail();
                      handleImpersonate(userDetailModal.user);
                    }}
                  >
                    <LogInIcon className="w-4 h-4 mr-2" />
                    Kullanıcıya Geç
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Impersonate Modal */}
      {impersonateModal.show && impersonateModal.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Kullanıcı Hesabına Geçiş</h3>
                <button
                  onClick={closeImpersonateModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>{impersonateModal.user.full_name}</strong> kullanıcısının hesabına geçiş yapmak üzeresiniz.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Email: {impersonateModal.user.email}
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <InfoIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-purple-900">Güvenlik Doğrulaması</p>
                      <p className="text-xs text-purple-700 mt-1">
                        Geçiş yapmak için Ultra Admin şifrenizi girmeniz gerekiyor.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Password Input */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Ultra Admin Şifresi</label>
                  <Input
                    type="password"
                    placeholder="Şifrenizi girin"
                    value={impersonateModal.password}
                    onChange={(e) => setImpersonateModal(prev => ({ ...prev, password: e.target.value }))}
                    disabled={impersonateModal.loading}
                    className="w-full"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && impersonateModal.password) {
                        performImpersonation();
                      }
                    }}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={closeImpersonateModal}
                    disabled={impersonateModal.loading}
                  >
                    İptal
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={performImpersonation}
                    disabled={impersonateModal.loading || !impersonateModal.password}
                  >
                    {impersonateModal.loading ? (
                      <>
                        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                        Doğrulanıyor...
                      </>
                    ) : (
                      <>
                        <LogInIcon className="w-4 h-4 mr-2" />
                        Geçiş Yap
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <KeyIcon className="w-8 h-8 text-blue-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Şifre Sıfırlandı
              </h3>
              
              <p className="text-gray-600 mb-6">
                <strong>{passwordModal.user?.full_name}</strong> kullanıcısı için yeni geçici şifre oluşturuldu.
              </p>

              {passwordModal.loading ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Şifre oluşturuluyor...</p>
                </div>
              ) : passwordModal.password ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700 mb-1">Geçici Şifre</p>
                      <code className="text-lg font-mono font-bold text-blue-900">
                        {passwordModal.password}
                      </code>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPassword(passwordModal.password)}
                      className="ml-2"
                    >
                      <CopyIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    ⏰ Bu şifre 24 saat geçerlidir. Kullanıcının ilk girişte değiştirmesi önerilir.
                  </p>
                </div>
              ) : null}

              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setPasswordModal({ show: false, user: null, password: null, loading: false })}
                >
                  Kapat
                </Button>
                {passwordModal.password && (
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      copyPassword(passwordModal.password);
                      setPasswordModal({ show: false, user: null, password: null, loading: false });
                    }}
                  >
                    Kopyala & Kapat
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Master Password Modal */}
      {masterPasswordModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <KeyIcon className="w-8 h-8 text-indigo-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Master Şifre
              </h3>
              
              <p className="text-gray-600 mb-6">
                <strong>{masterPasswordModal.user?.full_name}</strong> kullanıcısı için master şifre yönetimi
              </p>

              {masterPasswordModal.loading ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Kontrol ediliyor...</p>
                </div>
              ) : masterPasswordModal.password ? (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-indigo-700 mb-1">Master Şifre</p>
                      <code className="text-lg font-mono font-bold text-indigo-900">
                        {masterPasswordModal.password}
                      </code>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyMasterPassword(masterPasswordModal.password)}
                      className="ml-2"
                    >
                      <CopyIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                    <p className="text-xs text-yellow-800 font-medium">
                      ⚠️ Bu şifre kullanıcının normal şifresini etkilemez
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Bu şifre ile tüm kullanıcı hesaplarına erişebilirsiniz. Güvenli bir yerde saklayın.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  {masterPasswordModal.hasMasterPassword ? (
                    <div>
                      <p className="text-sm text-gray-700 mb-2">
                        ✅ Bu kullanıcı için master şifre mevcut
                      </p>
                      <p className="text-xs text-gray-600">
                        Yeni master şifre oluşturmak için "Yenile" butonuna tıklayın
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-700 mb-2">
                        ℹ️ Bu kullanıcı için henüz master şifre oluşturulmamış
                      </p>
                      <p className="text-xs text-gray-600">
                        Yeni master şifre oluşturmak için "Oluştur" butonuna tıklayın
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setMasterPasswordModal({ show: false, user: null, password: null, loading: false, hasMasterPassword: false })}
                >
                  Kapat
                </Button>
                {!masterPasswordModal.password && (
                  <Button 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={generateMasterPassword}
                    disabled={masterPasswordModal.loading}
                  >
                    {masterPasswordModal.hasMasterPassword ? 'Yenile' : 'Oluştur'}
                  </Button>
                )}
                {masterPasswordModal.password && (
                  <Button 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={() => {
                      copyMasterPassword(masterPasswordModal.password);
                      setMasterPasswordModal({ show: false, user: null, password: null, loading: false, hasMasterPassword: false });
                    }}
                  >
                    Kopyala & Kapat
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraUserManagementNew;
